import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useGeolocation } from '../hooks/useGeolocation'
import { useHunts } from '../hooks/useHunt'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { BottomNav } from '../components/common/BottomNav'
import { formatDistance } from '../hooks/useGeolocation'
import type { Hunt } from '../types'

// Compass icon component
function CompassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2" fill="none" />
      <circle cx="24" cy="24" r="4" fill="currentColor" />
      <path d="M24 8V4M24 44V40M8 24H4M44 24H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 12L28 24L24 28L20 24L24 12Z" fill="#C53030" />
      <path d="M24 36L20 24L24 20L28 24L24 36Z" fill="currentColor" />
    </svg>
  )
}

// Difficulty badge component with gradient styling
function DifficultyBadge({ difficulty }: { difficulty: Hunt['difficulty'] }) {
  const config = {
    easy: {
      emoji: '🌿',
      gradient: 'from-[#d0fae5] to-[#a4f4cf]',
      border: 'border-[#00d492]',
      shadow: 'shadow-[0px_1px_3px_0px_#5ee9b5,0px_1px_2px_-1px_#5ee9b5]',
      text: 'text-[#004f3b]',
      label: 'Easy',
    },
    medium: {
      emoji: '⚡',
      gradient: 'from-[#fef3c6] to-[#fee685]',
      border: 'border-gold-300',
      shadow: 'shadow-[0px_1px_3px_0px_#ffd230,0px_1px_2px_-1px_#ffd230]',
      text: 'text-brown-700',
      label: 'Medium',
    },
    hard: {
      emoji: '🔥',
      gradient: 'from-[#ffe4e6] to-[#ffccd3]',
      border: 'border-[#ff637e]',
      shadow: 'shadow-[0px_1px_3px_0px_#ffa1ad,0px_1px_2px_-1px_#ffa1ad]',
      text: 'text-[#8b0836]',
      label: 'Hard',
    },
  }
  const { emoji, gradient, border, shadow, text, label } = config[difficulty]

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-bold bg-gradient-to-r ${gradient} ${border} border ${shadow} ${text}`}>
      <span>{emoji}</span>
      {label}
    </span>
  )
}

// Horizontal hunt card for "Hunts Near You"
function HuntCardHorizontal({ hunt }: { hunt: Hunt }) {
  return (
    <Link to={`/hunts/${hunt.id}`} className="flex-shrink-0 w-64">
      <div className="bg-cream-50 border border-gold-100 rounded-2xl shadow-[0px_4px_12px_0px_rgba(217,119,6,0.15)] overflow-hidden transition-all duration-200 hover:shadow-[0px_8px_20px_0px_rgba(217,119,6,0.2)] hover:-translate-y-1">
        {/* Image section */}
        <div className="relative h-36">
          {hunt.cover_image_url ? (
            <img
              src={hunt.cover_image_url}
              alt={hunt.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-cream-100 to-cream-300" />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-brown-900/60 via-transparent to-transparent" />
          {/* Distance badge */}
          {hunt.distance_meters !== undefined && hunt.distance_meters > 0 && (
            <div className="absolute top-2 right-2 bg-white/95 rounded-full px-2.5 py-1 flex items-center gap-1.5 shadow-md">
              <svg className="w-3 h-3 text-brown-900" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-bold text-brown-900">{formatDistance(hunt.distance_meters)}</span>
            </div>
          )}
        </div>
        {/* Content section */}
        <div className="p-4 bg-gradient-to-b from-cream-50 to-cream-100/30">
          <h3 className="font-medium text-brown-900 mb-3 line-clamp-1">{hunt.title}</h3>
          <div className="flex items-center justify-between">
            <DifficultyBadge difficulty={hunt.difficulty} />
            <div className="flex items-center gap-3 text-xs text-brown-500">
              <span className="flex items-center gap-1">
                <span className="bg-gradient-to-b from-primary-600 to-primary-700 text-white text-[10px] px-1.5 py-0.5 rounded">📦</span>
                <span>{hunt.waypoint_count}</span>
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{hunt.total_starts}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

// Popular hunt list item
function PopularHuntItem({ hunt, rank }: { hunt: Hunt; rank: number }) {
  return (
    <Link to={`/hunts/${hunt.id}`}>
      <div className="bg-gradient-to-b from-cream-50 to-cream-100 border border-gold-100 rounded-2xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
        <div className="flex items-center gap-4 p-3">
          {/* Rank number with gradient */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-b from-primary-600 to-primary-700 border border-gold-200 shadow-md flex items-center justify-center">
            <span className="font-bold text-sm text-white">{rank}</span>
          </div>

          {/* Thumbnail with golden border */}
          <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 border-gold-200 shadow-sm">
            {hunt.cover_image_url ? (
              <img
                src={hunt.cover_image_url}
                alt={hunt.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-cream-100 to-cream-300" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-brown-900 mb-1 line-clamp-1">{hunt.title}</h3>
            <DifficultyBadge difficulty={hunt.difficulty} />
            <div className="flex items-center gap-3 mt-2 text-xs text-brown-500">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{hunt.total_starts}</span>
              </span>
              {hunt.estimated_duration_minutes && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{Math.floor(hunt.estimated_duration_minutes / 60)}h</span>
                </span>
              )}
            </div>
          </div>

          {/* Chevron */}
          <svg className="w-5 h-5 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  )
}

export default function Home() {
  const { user, isAuthenticated } = useAuth()
  const { location, requestLocation, loading: geoLoading, error: geoError } = useGeolocation()

  const { data: huntsData, isLoading: huntsLoading } = useHunts(
    location
      ? { lat: location.latitude, lon: location.longitude, radius: 50000 }
      : undefined
  )

  // Mock data for "Continue Your Hunt" - in real app this would come from user progress API
  // For demo purposes, always show the first hunt as "in progress"
  const activeHunt = huntsData?.items[0]
  const hasActiveHunt = !!activeHunt // Show for all users when there's hunt data

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 via-cream-100/50 to-cream-300 pb-24">
      {/* Header */}
      <header className="pt-4 pb-2">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo and title */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 text-primary-600">
                <CompassIcon className="w-full h-full" />
              </div>
              <div>
                <p className="text-sm text-primary-600 font-medium">Ready for adventure?</p>
                <h1 className="text-2xl font-bold text-brown-900">Loot Seeker</h1>
              </div>
            </div>

            {/* Profile button */}
            {isAuthenticated ? (
              <Link to="/profile">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center font-semibold text-primary-700 border-2 border-primary-200">
                  {user?.display_name?.[0]?.toUpperCase() || 'U'}
                </div>
              </Link>
            ) : (
              <Link to="/login">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 border-2 border-primary-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </Link>
            )}
          </div>

          {/* Location indicator */}
          {location && (
            <div className="flex items-center gap-1.5 mt-3 text-brown-600">
              <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Near your location</span>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-lg mx-auto px-4 py-4 space-y-6">
        {/* Location prompt if not enabled */}
        {!location && (
          <Card variant="parchment" padding="lg">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-brown-900 mb-2">
                Find hunts near you
              </h2>
              <p className="text-brown-600 mb-4">
                Enable location to discover treasure hunts in your area
              </p>
              {geoError && (
                <p className="text-red-600 text-sm mb-4">{geoError}</p>
              )}
              <Button onClick={requestLocation} loading={geoLoading}>
                Enable Location
              </Button>
            </div>
          </Card>
        )}

        {/* Continue Your Hunt */}
        {hasActiveHunt && (
          <Link to={`/hunts/${activeHunt.id}/play`}>
            <div className="relative bg-gradient-to-b from-cream-100 to-cream-300 border-2 border-primary-600 rounded-3xl shadow-[0px_8px_20px_0px_rgba(217,119,6,0.25)] overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-gradient-to-br from-brown-700 via-transparent to-brown-700" />
              <div className="absolute -top-2 right-5 w-4 h-4 rounded-full bg-error-400 opacity-20" />
              <div className="absolute top-0 right-6 w-3 h-3 rounded-full bg-error-400" />

              <div className="relative flex items-center gap-4 p-5">
                {/* Thumbnail with golden border */}
                <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 border-gold-300 shadow-md">
                  {activeHunt.cover_image_url ? (
                    <img
                      src={activeHunt.cover_image_url}
                      alt={activeHunt.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cream-100 to-cream-300" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-brown-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-brown-600 font-medium">Continue Your Hunt</span>
                    </div>
                    <svg className="w-5 h-5 text-brown-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <h3 className="font-medium text-brown-900 mb-2 line-clamp-1">{activeHunt.title}</h3>

                  {/* Progress */}
                  <div className="flex items-center justify-between text-xs text-brown-500 mb-1.5">
                    <span>4/{activeHunt.waypoint_count} waypoints</span>
                    <span className="font-bold text-primary-600">65%</span>
                  </div>
                  <div className="h-2.5 bg-gold-100 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-b from-primary-600 to-primary-500 rounded-full shadow-sm" style={{ width: '65%' }} />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Hunts Near You - Horizontal Scroll */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2 text-lg font-medium text-brown-900">
              <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              {location ? 'Hunts Near You' : 'Featured Hunts'}
            </h2>
            <Link to="/hunts" className="flex items-center gap-1 text-primary-600 text-sm font-medium hover:text-primary-700">
              See all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {huntsLoading ? (
            <LoadingSpinner className="py-8" />
          ) : huntsData?.items.length === 0 ? (
            <Card padding="lg">
              <p className="text-center text-brown-600">
                No hunts found nearby. Try expanding your search area.
              </p>
            </Card>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {huntsData?.items.slice(0, 5).map((hunt) => (
                <HuntCardHorizontal key={hunt.id} hunt={hunt} />
              ))}
            </div>
          )}
        </section>

        {/* Popular This Week */}
        <section>
          <h2 className="flex items-center gap-2 text-lg font-medium text-brown-900 mb-4">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Popular This Week
          </h2>

          {huntsLoading ? (
            <LoadingSpinner className="py-8" />
          ) : (
            <div className="space-y-3">
              {huntsData?.items.slice(0, 3).map((hunt, index) => (
                <PopularHuntItem key={hunt.id} hunt={hunt} rank={index + 1} />
              ))}
            </div>
          )}
        </section>

        {/* CTA for non-authenticated users */}
        {!isAuthenticated && (
          <Card variant="parchment" padding="lg">
            <div className="text-center">
              <h3 className="font-semibold text-brown-900 mb-2">
                Ready to start hunting?
              </h3>
              <p className="text-brown-600 text-sm mb-4">
                Create an account to join hunts, earn rewards, and track your progress.
              </p>
              <Link to="/register">
                <Button>Get Started</Button>
              </Link>
            </div>
          </Card>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  )
}
